import { useMutation } from '@apollo/client/react';
import { Button, Col, Form, Input, Modal, Row } from 'antd';
import { fill, filter } from 'lodash';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../../AppContext';
import countryCodeData from '../../../assets/countryCode.json';
import { WrongIcon } from '../../../assets/svg';
import {
  GA_EVENT,
  GA_LABEL,
  MOBILE_NUMBER_NOTE,
  REGEX,
  USER_ROLES,
} from '../../../common/constants';
import { Event } from '../../../common/trackEvents';
import { formValidatorRules } from '../../../common/utils';
import CommonCard from '../../../components/CommonCard';
import PhoneNumberInputWithCountyCode from '../../../components/PhoneNumberInputWithCountyCode';
import { UPDATE_USER } from '../../users/graphql/Mutations';
import { CREATE_AGENCY_CONTACT } from '../graphql/Mutation';

const { required, email } = formValidatorRules;

const AddContactModal = (props) => {
  const {
    isUpdate,
    showModal,
    setShowModal,
    agencyContactData,
    handleOnComplete,
    setAgencyContactData,
    agencyId,
    refetchAgencyContactDetails,
    onlyGetContactData = false,
    setUserList,
    userList,
  } = props;
  const [form] = Form.useForm();
  const { getCurrentUser } = useContext(AppContext);
  const [alreadyAddedUser, setAlreadyAddedUser] = useState();
  const currentUser = getCurrentUser();
  const handleCancel = () => {
    setShowModal(false);
    form.resetFields();
    setAgencyContactData();
  };

  const [createAgencyContact, { loading: createLoading }] = useMutation(
    CREATE_AGENCY_CONTACT,
    {
      onError() {},
      onCompleted(res) {
        setAlreadyAddedUser(res?.createUser?.user);
        if (!res?.createUser?.user) {
          Event(GA_EVENT.ADD_GLOBAL_AGENCY_CONTACT, {
            label: GA_LABEL.ADD_GLOBAL_AGENCY_CONTACT,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            agency_id: agencyId,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          setAgencyContactData();
          form.resetFields();
          setShowModal(false);
          refetchAgencyContactDetails();
        }
      },
    },
  );
  const [updateAgencyContact, { loading: updateLoading }] = useMutation(
    UPDATE_USER,
    {
      onError() {},
      onCompleted() {
        Event(GA_EVENT.EDIT_GLOBAL_AGENCY_CONTACT, {
          label: GA_LABEL.EDIT_GLOBAL_AGENCY_CONTACT,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          agency_id: agencyId,
          contact_id: agencyContactData?.id,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        setAgencyContactData();
        form.resetFields();
        setShowModal(false);
        refetchAgencyContactDetails();
      },
    },
  );

  const onFinish = async (formValues, isUpdateExistingUser = false) => {
    const newFormValues = {
      ...formValues,
      name: formValues?.name,
      designation: formValues?.designation,
      email: formValues?.email || null,
      phoneNo: `${formValues?.countryCode}${formValues?.phoneNo}`,
      country: filter(
        countryCodeData,
        (record) => record?.dialCode === formValues?.countryCode,
      )?.[0]?.countryCode?.toUpperCase(),
    };
    if (onlyGetContactData) {
      if (isUpdate) {
        fill(userList, newFormValues, agencyId, Number(agencyId) + 1);
      } else {
        setUserList([...userList, newFormValues]);
      }
      setAgencyContactData();
      setShowModal(false);
      handleOnComplete();
    } else {
      const variables = isUpdate
        ? {
            data: {
              ...newFormValues,
              roles: [USER_ROLES.USER],
            },
            id: agencyContactData?.id,
          }
        : {
            ...newFormValues,
            useExistingUser: !!alreadyAddedUser && !isUpdateExistingUser,
            updateExistingUser: isUpdateExistingUser,
            roles: [USER_ROLES.USER],
          };

      try {
        if (isUpdate) {
          await updateAgencyContact({
            variables: { ...variables },
          });
          return;
        }
        await createAgencyContact({
          variables: {
            data: {
              ...variables,
              isAgencyUser: true,
              agencyIds: [agencyId],
            },
          },
        });
      } catch (error) {
        return error;
      }
    }
  };

  return (
    <div id="add-edit-team-user">
      <Modal
        maskClosable={false}
        centered
        open={showModal}
        className="dialog"
        footer={null}
        onCancel={handleCancel}
        closeIcon={<WrongIcon />}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('add-edit-team-user')
        }
      >
        <h2 id="add-contact-modal-title">
          {isUpdate ? 'Edit Team User' : 'Add Team User'}
        </h2>
        <Form
          id="add-contact-form"
          form={form}
          initialValues={
            agencyContactData
              ? {
                  ...agencyContactData,
                  phoneNo: agencyContactData?.phoneNo?.replace(
                    agencyContactData?.countryCode,
                    '',
                  ),
                }
              : { countryCode: '+91' }
          }
          layout="vertical"
          onValuesChange={() => {
            if (alreadyAddedUser) {
              setAlreadyAddedUser();
            }
          }}
          onFinish={onFinish}
        >
          <Form.Item
            rules={[
              required,
              {
                max: 250,
                message: 'Name cannot be more than 250 characters',
              },
            ]}
            name="name"
            label="Name"
          >
            <Input
              id="contact-name-input"
              allowClear
              placeholder="Enter Name"
            />
          </Form.Item>
          <Form.Item
            rules={[
              email,
              {
                max: 250,
                message: 'Email cannot be more than 250 characters',
              },
            ]}
            name="email"
            label="Email"
          >
            <Input
              id="contact-email-input"
              allowClear
              placeholder="Enter Email"
              onKeyDown={(e) => {
                if (e?.code === 'Space') {
                  e?.preventDefault();
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label={
              <>
                <span className="required-mark">*</span> Mobile Number
              </>
            }
            name="phoneNo"
            dependencies={['countryCode']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!getFieldValue('countryCode')) {
                    return Promise.reject(
                      new Error('Please Select Country Code'),
                    );
                  }
                  if (!value) {
                    return Promise.reject(new Error('Enter Mobile Number'));
                  }
                  if (value && !value.match(REGEX.PHONE)) {
                    return Promise.reject(
                      new Error('should be a valid contact number'),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <PhoneNumberInputWithCountyCode id="contact-phone-input" />
          </Form.Item>
          <div
            id="mobile-number-note"
            className="text-danger note-text note-margin"
          >
            Note : {MOBILE_NUMBER_NOTE}
          </div>
          {alreadyAddedUser && (
            <div id="existing-user-info" className="existing-user-info">
              <h3 id="existing-user-title">User Details Already Exist</h3>
              <p>The details you entered match below user's existing record.</p>
              <CommonCard
                id="existing-user-card"
                variant="outlined"
                className="existing-user-info-card"
              >
                <div>
                  <div>
                    <b>Name : </b> {alreadyAddedUser?.name || '-'}
                  </div>
                  <div>
                    <b>Email : </b> {alreadyAddedUser?.email || '-'}
                  </div>
                  <div>
                    <b>Mobile Number : </b>
                    {alreadyAddedUser?.phoneNo || '-'}
                  </div>
                  <p>
                    Either keep the same details or enter different details to
                    create the user.
                  </p>
                </div>
              </CommonCard>
            </div>
          )}
          <div className="d-flex justify-end align-center form-buttons">
            {alreadyAddedUser ? (
              <Row
                justify={
                  !alreadyAddedUser?.isActive &&
                  !alreadyAddedUser?.isUserExistWithSameEmailAndPhone
                    ? 'space-between'
                    : 'end'
                }
                align="middle"
                className="width-percent-100"
                gutter={10}
              >
                {!alreadyAddedUser?.isActive &&
                  !alreadyAddedUser?.isUserExistWithSameEmailAndPhone && (
                    <Col span={12}>
                      <Button
                        id="update-with-entered-details-btn"
                        disabled={false}
                        shape="round"
                        onClick={() => {
                          const formValues = form.getFieldsValue();
                          onFinish(formValues, true);
                        }}
                        type="link"
                        className="width-percent-100"
                      >
                        Update With Entered Details
                      </Button>
                    </Col>
                  )}
                <Col span={12}>
                  <Button
                    id="add-with-existing-details-btn"
                    disabled={false}
                    type="primary"
                    shape="round"
                    className="width-percent-100"
                    loading={createLoading || updateLoading}
                    onClick={() => {
                      form.submit();
                    }}
                  >
                    Add With Existing Details
                  </Button>
                </Col>
              </Row>
            ) : (
              <Button
                id="add-contact-save-btn"
                shape="round"
                className="save-button"
                type="primary"
                htmlType="submit"
                loading={createLoading || updateLoading}
              >
                {isUpdate ? 'Save' : 'Add'}
              </Button>
            )}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddContactModal;
